import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  CalendarIcon, 
  Heart, 
  Apple, 
  Dumbbell, 
  Award, 
  Shield, 
  Plus,
  Filter,
  Clock,
  MapPin,
  User,
  Stethoscope,
  X
} from 'lucide-react';
import { format, parseISO, isValid, addDays, startOfWeek, addWeeks, subWeeks, isSameDay, isToday, startOfMonth, endOfMonth, addMonths, subMonths, eachDayOfInterval, getDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Calendar as CalendarLucide, Eye } from 'lucide-react';

interface CalendarSectionProps {
  data: Record<string, any>;
  onChange: (data: Record<string, any>) => void;
}

interface CalendarEvent {
  id: string;
  title: string;
  date: string;
  type: 'health' | 'nutrition' | 'exercise' | 'training' | 'documents' | 'custom';
  description?: string;
  location?: string;
  reminder?: boolean;
  completed?: boolean;
  time?: string;
}

// Horizontal Agenda Calendar Component
interface HorizontalAgendaCalendarProps {
  selectedDate: Date | undefined;
  onDateSelect: (date: Date | undefined) => void;
  datesWithEvents: Date[];
  events: CalendarEvent[];
}

const HorizontalAgendaCalendar = ({ selectedDate, onDateSelect, datesWithEvents, events }: HorizontalAgendaCalendarProps) => {
  const [currentWeek, setCurrentWeek] = useState(() => startOfWeek(selectedDate || new Date(), { weekStartsOn: 1 }));
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(selectedDate || new Date()));
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
  const [selectedDayEvents, setSelectedDayEvents] = useState<{ date: Date; events: CalendarEvent[] } | null>(null);
  
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeek, i));
  
  // Generate days for month view
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = addDays(startDate, 41); // 6 weeks = 42 days
  const monthDays = eachDayOfInterval({ start: startDate, end: endDate });
  
  const goToPreviousWeek = () => {
    setCurrentWeek(subWeeks(currentWeek, 1));
  };
  
  const goToNextWeek = () => {
    setCurrentWeek(addWeeks(currentWeek, 1));
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };
  
  const goToNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };
  
  const goToToday = () => {
    const today = new Date();
    if (viewMode === 'week') {
      setCurrentWeek(startOfWeek(today, { weekStartsOn: 1 }));
    } else {
      setCurrentMonth(startOfMonth(today));
    }
    onDateSelect(today);
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === 'week' ? 'month' : 'week');
  };

  const openDayEvents = (date: Date) => {
    const dayEvents = getEventsForDate(date);
    setSelectedDayEvents({ date, events: dayEvents });
  };
  
  const getEventsForDate = (date: Date) => {
    return events.filter(event => {
      if (!event.date) return false;
      try {
        const eventDate = parseISO(event.date);
        return isValid(eventDate) && isSameDay(eventDate, date);
      } catch {
        return false;
      }
    });
  };
  
  const hasEvents = (date: Date) => {
    return datesWithEvents.some(eventDate => isSameDay(eventDate, date));
  };

  return (
    <>
      <div className="bg-background/90 backdrop-blur rounded-lg p-4 border border-border/20">
        {/* Navigation Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={viewMode === 'week' ? goToPreviousWeek : goToPreviousMonth}
              className="h-8 w-8 p-0 hover:bg-primary/10"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <h3 className="text-lg font-semibold text-foreground">
              {viewMode === 'week' 
                ? format(currentWeek, 'MMMM yyyy', { locale: it })
                : format(currentMonth, 'MMMM yyyy', { locale: it })
              }
            </h3>
            
            <Button
              variant="outline"
              size="sm"
              onClick={viewMode === 'week' ? goToNextWeek : goToNextMonth}
              className="h-8 w-8 p-0 hover:bg-primary/10"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleViewMode}
              className="text-xs px-3 hover:bg-primary/10"
            >
              <CalendarLucide className="w-3 h-3 mr-1" />
              {viewMode === 'week' ? 'Mese' : 'Settimana'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={goToToday}
              className="text-xs px-3 hover:bg-primary/10"
            >
              Oggi
            </Button>
          </div>
        </div>

        {viewMode === 'week' ? (
          /* Horizontal Week View */
          <div className="grid grid-cols-7 gap-2">
            {weekDays.map((day, index) => {
              const dayEvents = getEventsForDate(day);
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              const isCurrentDay = isToday(day);
              const hasEventsDay = hasEvents(day);
              
              return (
                <div key={index} className="min-h-[120px]">
                  {/* Day Header */}
                  <button
                    onClick={() => onDateSelect(day)}
                    className={`w-full p-3 rounded-t-lg border-2 transition-all duration-200 ${
                      isSelected
                        ? 'bg-primary text-primary-foreground border-primary shadow-lg scale-105'
                        : isCurrentDay
                        ? 'bg-secondary/50 text-secondary-foreground border-secondary hover:bg-secondary/70'
                        : hasEventsDay
                        ? 'bg-accent/30 text-accent-foreground border-accent/50 hover:bg-accent/50'
                        : 'bg-muted/20 text-muted-foreground border-muted hover:bg-muted/40'
                    }`}
                  >
                    <div className="text-xs font-medium opacity-70">
                      {format(day, 'EEE', { locale: it })}
                    </div>
                    <div className="text-lg font-bold mt-1">
                      {format(day, 'd')}
                    </div>
                    {hasEventsDay && (
                      <div className="w-2 h-2 bg-primary rounded-full mx-auto mt-1" />
                    )}
                  </button>
                  
                  {/* Events List for this day */}
                  <div className="bg-muted/10 rounded-b-lg border-l-2 border-r-2 border-b-2 border-muted/20 p-2 min-h-[60px] relative">
                    <div className="space-y-1">
                      {dayEvents.slice(0, 2).map((event, eventIndex) => (
                        <div
                          key={eventIndex}
                          className={`text-xs p-2 rounded-md truncate cursor-pointer transition-all hover:scale-105 ${
                            event.type === 'health' ? 'bg-red-500/20 text-red-200 border border-red-500/30' :
                            event.type === 'nutrition' ? 'bg-green-500/20 text-green-200 border border-green-500/30' :
                            event.type === 'exercise' ? 'bg-blue-500/20 text-blue-200 border border-blue-500/30' :
                            event.type === 'training' ? 'bg-purple-500/20 text-purple-200 border border-purple-500/30' :
                            event.type === 'documents' ? 'bg-yellow-500/20 text-yellow-200 border border-yellow-500/30' :
                            'bg-gray-500/20 text-gray-200 border border-gray-500/30'
                          }`}
                        >
                          <div className="font-medium">{event.title}</div>
                          {event.time && (
                            <div className="text-xs opacity-70 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {event.time}
                            </div>
                          )}
                        </div>
                      ))}
                      {dayEvents.length > 2 && (
                        <button 
                          onClick={() => openDayEvents(day)}
                          className="text-xs text-primary hover:text-primary-glow text-center py-1 w-full hover:bg-primary/10 rounded transition-all"
                        >
                          +{dayEvents.length - 2} altri
                        </button>
                      )}
                      {dayEvents.length > 0 && (
                        <button 
                          onClick={() => openDayEvents(day)}
                          className="absolute top-1 right-1 opacity-70 hover:opacity-100 transition-all"
                        >
                          <Eye className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Month View */
          <div className="space-y-4">
            {/* Days of week header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((day) => (
                <div key={day} className="text-center text-xs font-medium text-muted-foreground p-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Month calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {monthDays.map((day, index) => {
                const dayEvents = getEventsForDate(day);
                const isSelected = selectedDate && isSameDay(day, selectedDate);
                const isCurrentDay = isToday(day);
                const hasEventsDay = hasEvents(day);
                const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                
                return (
                  <button
                    key={index}
                    onClick={() => {
                      onDateSelect(day);
                      if (dayEvents.length > 0) {
                        openDayEvents(day);
                      }
                    }}
                    className={`relative aspect-square p-1 text-sm rounded-lg border transition-all duration-200 hover:scale-105 ${
                      isSelected
                        ? 'bg-primary text-primary-foreground border-primary shadow-lg'
                        : isCurrentDay
                        ? 'bg-secondary/50 text-secondary-foreground border-secondary'
                        : hasEventsDay
                        ? 'bg-accent/30 text-accent-foreground border-accent/50 hover:bg-accent/50'
                        : isCurrentMonth
                        ? 'bg-muted/20 text-muted-foreground border-muted hover:bg-muted/40'
                        : 'bg-muted/10 text-muted-foreground/50 border-muted/20'
                    }`}
                  >
                    <div className="font-medium">{format(day, 'd')}</div>
                    {hasEventsDay && (
                      <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2">
                        <div className="flex gap-0.5">
                          {dayEvents.slice(0, 3).map((event, i) => (
                            <div 
                              key={i}
                              className={`w-1.5 h-1.5 rounded-full ${
                                event.type === 'health' ? 'bg-red-400' :
                                event.type === 'nutrition' ? 'bg-green-400' :
                                event.type === 'exercise' ? 'bg-blue-400' :
                                event.type === 'training' ? 'bg-purple-400' :
                                event.type === 'documents' ? 'bg-yellow-400' :
                                'bg-gray-400'
                              }`}
                            />
                          ))}
                          {dayEvents.length > 3 && (
                            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                          )}
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Selected Date Info */}
        {selectedDate && (
          <div className="mt-4 p-3 bg-primary/10 rounded-lg border border-primary/20">
            <div className="text-sm font-medium text-primary">
              Data selezionata: {format(selectedDate, 'dd MMMM yyyy', { locale: it })}
            </div>
          </div>
        )}
      </div>

      {/* Day Events Modal */}
      <Dialog open={selectedDayEvents !== null} onOpenChange={() => setSelectedDayEvents(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto glass border-white/20">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" />
              Eventi del {selectedDayEvents && format(selectedDayEvents.date, 'dd MMMM yyyy', { locale: it })}
            </DialogTitle>
          </DialogHeader>
          
          {selectedDayEvents && (
            <div className="space-y-4 mt-4">
              {selectedDayEvents.events.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  Nessun evento per questa data
                </p>
              ) : (
                selectedDayEvents.events.map((event, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border transition-all hover:scale-[1.02] ${
                      event.type === 'health' ? 'bg-red-500/20 border-red-500/30' :
                      event.type === 'nutrition' ? 'bg-green-500/20 border-green-500/30' :
                      event.type === 'exercise' ? 'bg-blue-500/20 border-blue-500/30' :
                      event.type === 'training' ? 'bg-purple-500/20 border-purple-500/30' :
                      event.type === 'documents' ? 'bg-yellow-500/20 border-yellow-500/30' :
                      'bg-gray-500/20 border-gray-500/30'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-white">{event.title}</h4>
                        {event.description && (
                          <p className="text-sm opacity-80 mt-1">{event.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-sm opacity-70">
                          {event.time && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {event.time}
                            </div>
                          )}
                          {event.location && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.location}
                            </div>
                          )}
                        </div>
                      </div>
                      <Badge 
                        variant="outline" 
                        className={`ml-2 ${
                          event.type === 'health' ? 'border-red-500/50 text-red-200' :
                          event.type === 'nutrition' ? 'border-green-500/50 text-green-200' :
                          event.type === 'exercise' ? 'border-blue-500/50 text-blue-200' :
                          event.type === 'training' ? 'border-purple-500/50 text-purple-200' :
                          event.type === 'documents' ? 'border-yellow-500/50 text-yellow-200' :
                          'border-gray-500/50 text-gray-200'
                        }`}
                      >
                        {event.type === 'health' ? 'Salute' :
                         event.type === 'nutrition' ? 'Nutrizione' :
                         event.type === 'exercise' ? 'Esercizio' :
                         event.type === 'training' ? 'Training' :
                         event.type === 'documents' ? 'Documenti' :
                         'Custom'}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const CalendarSection = ({ data, onChange }: CalendarSectionProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: '',
    type: 'custom' as CalendarEvent['type'],
    description: '',
    location: '',
    reminder: false,
    frequency: 'specific',
    days: [] as string[],
    monthly_day: '',
    custom_dates: [] as string[]
  });

  // Generate recurring events based on frequency settings
  const generateRecurringEvents = (baseEvent: any, frequency: string, startDate: string, endDate?: string) => {
    const events: CalendarEvent[] = [];
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000); // 1 year from now if no end date
    
    switch (frequency) {
      case 'daily':
        for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
          events.push({
            ...baseEvent,
            date: date.toISOString().split('T')[0],
            id: `${baseEvent.id}-${date.toISOString().split('T')[0]}`
          });
        }
        break;
      case 'weekly':
        if (baseEvent.days && baseEvent.days.length > 0) {
          const dayNumbers: Record<string, number> = { Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 0 };
          for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
            const dayName = Object.keys(dayNumbers)[date.getDay() === 0 ? 6 : date.getDay() - 1];
            if (baseEvent.days.includes(dayName)) {
              events.push({
                ...baseEvent,
                date: date.toISOString().split('T')[0],
                id: `${baseEvent.id}-${date.toISOString().split('T')[0]}`
              });
            }
          }
        }
        break;
      case 'monthly':
        if (baseEvent.monthly_day) {
          for (let date = new Date(start); date <= end; date.setMonth(date.getMonth() + 1)) {
            const monthlyDate = new Date(date.getFullYear(), date.getMonth(), parseInt(baseEvent.monthly_day));
            if (monthlyDate <= end) {
              events.push({
                ...baseEvent,
                date: monthlyDate.toISOString().split('T')[0],
                id: `${baseEvent.id}-${monthlyDate.toISOString().split('T')[0]}`
              });
            }
          }
        }
        break;
      case 'specific':
        if (baseEvent.custom_dates) {
          baseEvent.custom_dates.forEach((date: string, index: number) => {
            if (date) {
              events.push({
                ...baseEvent,
                date: date,
                id: `${baseEvent.id}-specific-${index}`
              });
            }
          });
        }
        break;
    }
    
    return events;
  };

  // Extract all events with dates from passport data
  const extractEventsFromData = (passportData: Record<string, any>) => {
    const events: CalendarEvent[] = [];

    // Health events - Vaccinations
    if (passportData.health?.vaccinations) {
      passportData.health.vaccinations.forEach((vaccination: any, index: number) => {
        if (vaccination.admin_date) {
          events.push({
            id: `vaccination-${index}`,
            title: `Vaccinazione: ${vaccination.vaccine}`,
            date: vaccination.admin_date,
            type: 'health',
            description: `Produttore: ${vaccination.manufacturer || 'N/A'}`
          });
        }
        if (vaccination.next_due) {
          events.push({
            id: `vaccination-due-${index}`,
            title: `Richiamo: ${vaccination.vaccine}`,
            date: vaccination.next_due,
            type: 'health',
            description: 'Promemoria vaccinazione'
          });
        }
      });
    }

    // Health events - Medical checkups
    if (passportData.health?.checkups) {
      passportData.health.checkups.forEach((checkup: any, index: number) => {
        if (checkup.date) {
          events.push({
            id: `checkup-${index}`,
            title: `Controllo: ${checkup.type || 'Medico'}`,
            date: checkup.date,
            type: 'health',
            description: `Peso: ${checkup.weight || 'N/A'} kg, Veterinario: ${checkup.veterinarian || 'N/A'}`
          });
        }
        if (checkup.next_appointment) {
          events.push({
            id: `checkup-next-${index}`,
            title: `Prossimo controllo: ${checkup.type || 'Medico'}`,
            date: checkup.next_appointment,
            type: 'health',
            description: `Prenotato con: ${checkup.veterinarian || 'N/A'}`
          });
        }
      });
    }

    // Nutrition events - feeding schedule with frequency support
    if (passportData.nutrition?.feeding_schedule) {
      passportData.nutrition.feeding_schedule.forEach((meal: any, index: number) => {
        const baseEvent = {
          id: `meal-${index}`,
          title: `Pasto: ${meal.food_type || 'Non specificato'}`,
          type: 'nutrition' as const,
          description: `Orario: ${meal.time}, Quantità: ${meal.quantity || 'N/A'}`,
          time: meal.time
        };

        if (meal.frequency && meal.frequency !== 'specific') {
          const startDate = meal.date || new Date().toISOString().split('T')[0];
          const recurringEvents = generateRecurringEvents({ ...baseEvent, ...meal }, meal.frequency, startDate);
          events.push(...recurringEvents);
        } else if (meal.date) {
          events.push({
            ...baseEvent,
            date: meal.date
          });
        } else if (meal.custom_dates) {
          meal.custom_dates.forEach((date: string, dateIndex: number) => {
            if (date) {
              events.push({
                ...baseEvent,
                id: `${baseEvent.id}-custom-${dateIndex}`,
                date: date
              });
            }
          });
        }
      });
    }

    // Nutrition - Product expiry dates
    if (passportData.nutrition?.main_products) {
      passportData.nutrition.main_products.forEach((product: any, index: number) => {
        if (product.expiry_date) {
          events.push({
            id: `product-expiry-${index}`,
            title: `Scadenza: ${product.brand} - ${product.product_name}`,
            date: product.expiry_date,
            type: 'nutrition',
            description: 'Prodotto alimentare in scadenza'
          });
        }
        if (product.end_date) {
          events.push({
            id: `product-end-${index}`,
            title: `Fine utilizzo: ${product.brand} - ${product.product_name}`,
            date: product.end_date,
            type: 'nutrition',
            description: 'Fine periodo di utilizzo programmato'
          });
        }
      });
    }

    // Exercise events with frequency support
    if (passportData.exercise_wellbeing?.activity_plan) {
      passportData.exercise_wellbeing.activity_plan.forEach((activity: any, index: number) => {
        const baseEvent = {
          id: `exercise-${index}`,
          title: `Esercizio: ${activity.activity}`,
          type: 'exercise' as const,
          description: `Durata: ${activity.avg_duration_min} min, Intensità: ${activity.intensity}`
        };

        if (activity.frequency && activity.frequency !== 'specific') {
          const startDate = activity.scheduled_date || new Date().toISOString().split('T')[0];
          const recurringEvents = generateRecurringEvents({ ...baseEvent, ...activity }, activity.frequency, startDate);
          events.push(...recurringEvents);
        } else if (activity.scheduled_date) {
          events.push({
            ...baseEvent,
            date: activity.scheduled_date
          });
        } else if (activity.custom_dates) {
          activity.custom_dates.forEach((date: string, dateIndex: number) => {
            if (date) {
              events.push({
                ...baseEvent,
                id: `${baseEvent.id}-custom-${dateIndex}`,
                date: date
              });
            }
          });
        }
      });
    }

    // Training sessions with frequency support
    if (passportData.exercise_wellbeing?.training_sessions) {
      passportData.exercise_wellbeing.training_sessions.forEach((session: any, index: number) => {
        const baseEvent = {
          id: `training-${index}`,
          title: `Addestramento: ${session.activity}`,
          type: 'training' as const,
          description: `Durata: ${session.duration_minutes} min, Trainer: ${session.trainer || 'N/A'}`
        };

        if (session.frequency && session.start_date) {
          const recurringEvents = generateRecurringEvents({ ...baseEvent, ...session }, session.frequency, session.start_date, session.end_date);
          events.push(...recurringEvents);
        } else if (session.custom_dates) {
          session.custom_dates.forEach((date: string, dateIndex: number) => {
            if (date) {
              events.push({
                ...baseEvent,
                id: `${baseEvent.id}-custom-${dateIndex}`,
                date: date
              });
            }
          });
        }
      });
    }

    // Training competitions
    if (passportData.competitions?.events) {
      passportData.competitions.events.forEach((event: any, index: number) => {
        if (event.date) {
          events.push({
            id: `competition-${index}`,
            title: `Gara: ${event.name}`,
            date: event.date,
            type: 'training',
            description: `${event.discipline || ''} - ${event.location || ''}`
          });
        }
      });
    }

    // Document expiry dates
    if (passportData.documents_media?.files) {
      passportData.documents_media.files.forEach((doc: any, index: number) => {
        if (doc.expiry_date) {
          events.push({
            id: `doc-expiry-${index}`,
            title: `Scadenza: ${doc.title || 'Documento'}`,
            date: doc.expiry_date,
            type: 'documents',
            description: `Documento ${doc.type || ''} in scadenza`
          });
        }
        
        if (doc.expiry_date && doc.reminder_days) {
          const expiryDate = new Date(doc.expiry_date);
          const reminderDate = new Date(expiryDate);
          reminderDate.setDate(reminderDate.getDate() - parseInt(doc.reminder_days));
          
          if (reminderDate >= new Date()) {
            events.push({
              id: `doc-reminder-${index}`,
              title: `Promemoria: ${doc.title || 'Documento'}`,
              date: reminderDate.toISOString().split('T')[0],
              type: 'documents',
              description: `Scade tra ${doc.reminder_days} giorni`
            });
          }
        }
      });
    }

    // Calendar-specific custom events
    if (passportData.calendar?.events) {
      passportData.calendar.events.forEach((event: any, index: number) => {
        if (event.date) {
          events.push({
            id: `calendar-${index}`,
            title: event.title,
            date: event.date,
            type: 'custom',
            description: event.description,
            location: event.location,
            reminder: event.reminder,
            completed: event.completed || false
          });
        }
      });
    }

    return events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  // Extract all events
  const allEvents = useMemo(() => {
    return extractEventsFromData(data);
  }, [data]);

  // Filter events by type
  const filteredEvents = useMemo(() => {
    if (filterType === 'all') return allEvents;
    return allEvents.filter(event => event.type === filterType);
  }, [allEvents, filterType]);

  // Get events for selected date
  const eventsForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
    return filteredEvents.filter(event => event.date === selectedDateStr);
  }, [filteredEvents, selectedDate]);

  // Get dates with events for calendar highlighting
  const datesWithEvents = useMemo(() => {
    return filteredEvents.map(event => {
      const date = parseISO(event.date);
      return isValid(date) ? date : null;
    }).filter(Boolean) as Date[];
  }, [filteredEvents]);

  const eventTypeIcons = {
    health: Heart,
    nutrition: Apple,
    exercise: Dumbbell,
    training: Award,
    documents: Shield,
    custom: Clock
  };

  const eventTypeColors = {
    health: 'bg-red-100 text-red-800 border-red-200',
    nutrition: 'bg-green-100 text-green-800 border-green-200',
    exercise: 'bg-orange-100 text-orange-800 border-orange-200',
    training: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    documents: 'bg-purple-100 text-purple-800 border-purple-200',
    custom: 'bg-gray-100 text-gray-800 border-gray-200'
  };

  const eventTypeLabels = {
    health: 'Salute',
    nutrition: 'Nutrizione',
    exercise: 'Esercizio',
    training: 'Addestramento',
    documents: 'Documenti',
    custom: 'Personalizzato'
  };

  const addNewEvent = () => {
    const calendarEvents = data.calendar?.events || [];
    const newCalendarEvent = {
      ...newEvent,
      id: `manual-${Date.now()}`,
      completed: false
    };

    calendarEvents.push(newCalendarEvent);
    
    onChange({
      ...data,
      calendar: {
        ...data.calendar,
        events: calendarEvents
      }
    });

    setNewEvent({
      title: '',
      date: '',
      type: 'custom',
      description: '',
      location: '',
      reminder: false,
      frequency: 'specific',
      days: [],
      monthly_day: '',
      custom_dates: []
    });
    setShowAddEvent(false);
  };

  const toggleEventCompleted = (eventId: string) => {
    if (eventId.startsWith('calendar-')) {
      const index = parseInt(eventId.split('-')[1]);
      const calendarEvents = [...(data.calendar?.events || [])];
      calendarEvents[index] = {
        ...calendarEvents[index],
        completed: !calendarEvents[index].completed
      };
      
      onChange({
        ...data,
        calendar: {
          ...data.calendar,
          events: calendarEvents
        }
      });
    }
  };

  return (
    <div className="space-y-6 pb-8">
      <div className="flex items-center gap-3 mb-6">
        <CalendarIcon className="w-6 h-6 text-blue-500" />
        <h2 className="text-2xl font-bold text-white">Calendario del Passaporto</h2>
      </div>

      {/* Header with filters and add button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48 bg-background/60 backdrop-blur-sm border-white/20">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background/95 backdrop-blur-sm border-white/20 z-50">
              <SelectItem value="all">Tutti gli eventi</SelectItem>
              <SelectItem value="health">Salute</SelectItem>
              <SelectItem value="nutrition">Nutrizione</SelectItem>
              <SelectItem value="exercise">Esercizio</SelectItem>
              <SelectItem value="training">Addestramento</SelectItem>
              <SelectItem value="documents">Documenti</SelectItem>
              <SelectItem value="custom">Personalizzati</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Dialog open={showAddEvent} onOpenChange={setShowAddEvent}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Evento
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Nuovo Evento</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">Titolo *</Label>
                  <Input
                    id="title"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    placeholder="Nome dell'evento"
                  />
                </div>
                <div>
                  <Label htmlFor="type">Tipo</Label>
                  <Select value={newEvent.type} onValueChange={(value) => setNewEvent({ ...newEvent, type: value as CalendarEvent['type'] })}>
                    <SelectTrigger className="bg-background/60 backdrop-blur-sm border-white/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-background/95 backdrop-blur-sm border-white/20 z-50">
                      <SelectItem value="health">Controllo Medico</SelectItem>
                      <SelectItem value="nutrition">Programma Alimentare</SelectItem>
                      <SelectItem value="exercise">Piano Esercizi</SelectItem>
                      <SelectItem value="training">Sessione Addestramento</SelectItem>
                      <SelectItem value="documents">Promemoria Documento</SelectItem>
                      <SelectItem value="custom">Evento Personalizzato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="frequency">Frequenza</Label>
                <Select value={newEvent.frequency} onValueChange={(value) => setNewEvent({ ...newEvent, frequency: value })}>
                  <SelectTrigger className="bg-background/60 backdrop-blur-sm border-white/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-background/95 backdrop-blur-sm border-white/20 z-50">
                    <SelectItem value="specific">Data Specifica</SelectItem>
                    <SelectItem value="daily">Giornaliero</SelectItem>
                    <SelectItem value="weekly">Settimanale</SelectItem>
                    <SelectItem value="monthly">Mensile</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {newEvent.frequency === 'specific' && (
                <div>
                  <Label htmlFor="date">Data *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                  />
                </div>
              )}

              {newEvent.frequency === 'weekly' && (
                <div>
                  <Label>Giorni della Settimana</Label>
                  <div className="grid grid-cols-3 md:grid-cols-7 gap-2 mt-2">
                    {['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'].map((day, dayIndex) => (
                      <label key={day} className="flex items-center gap-1 text-sm">
                        <input
                          type="checkbox"
                          checked={newEvent.days.includes(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][dayIndex])}
                          onChange={(e) => {
                            const englishDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                            const englishDay = englishDays[dayIndex];
                            if (e.target.checked) {
                              setNewEvent({ ...newEvent, days: [...newEvent.days, englishDay] });
                            } else {
                              setNewEvent({ ...newEvent, days: newEvent.days.filter(d => d !== englishDay) });
                            }
                          }}
                        />
                        {day.slice(0, 3)}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {newEvent.frequency === 'monthly' && (
                <div>
                  <Label htmlFor="monthly_day">Giorno del Mese</Label>
                  <Input
                    id="monthly_day"
                    type="number"
                    min="1"
                    max="31"
                    value={newEvent.monthly_day}
                    onChange={(e) => setNewEvent({ ...newEvent, monthly_day: e.target.value })}
                    placeholder="es. 15 (ogni 15 del mese)"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="description">Descrizione</Label>
                  <Textarea
                    id="description"
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    placeholder="Dettagli dell'evento"
                  />
                </div>
                <div>
                  <Label htmlFor="location">Luogo</Label>
                  <Input
                    id="location"
                    value={newEvent.location}
                    onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                    placeholder="Dove si svolge l'evento"
                  />
                </div>
              </div>

              <Button 
                onClick={addNewEvent} 
                className="w-full" 
                disabled={!newEvent.title || (newEvent.frequency === 'specific' && !newEvent.date) || (newEvent.frequency === 'monthly' && !newEvent.monthly_day)}
              >
                Aggiungi Evento
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Horizontal Agenda Calendar */}
      <Card className="glass border-white/20 mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <CalendarIcon className="w-5 h-5" />
            Agenda del Passaporto
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <HorizontalAgendaCalendar
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
            datesWithEvents={datesWithEvents}
            events={allEvents}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Events for selected date */}
        <Card className="glass border-white/20">
          <CardHeader>
            <CardTitle className="text-white">
              Eventi per {selectedDate ? format(selectedDate, 'dd MMMM yyyy', { locale: it }) : 'nessuna data selezionata'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {eventsForSelectedDate.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Nessun evento per questa data
              </p>
            ) : (
              <div className="space-y-3">
                {eventsForSelectedDate.map((event) => {
                  const IconComponent = eventTypeIcons[event.type];
                  return (
                    <div
                      key={event.id}
                      className={`p-3 rounded-lg border ${eventTypeColors[event.type]} ${
                        event.completed ? 'opacity-60' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-2 flex-1">
                          <IconComponent className="w-4 h-4 mt-0.5" />
                          <div className="flex-1">
                            <h4 className="font-medium">{event.title}</h4>
                            {event.time && (
                              <p className="text-sm opacity-80">Orario: {event.time}</p>
                            )}
                            {event.description && (
                              <p className="text-sm opacity-80 mt-1">{event.description}</p>
                            )}
                            {event.location && (
                              <div className="flex items-center gap-1 text-sm opacity-80 mt-1">
                                <MapPin className="w-3 h-3" />
                                {event.location}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {eventTypeLabels[event.type]}
                          </Badge>
                          {event.id.startsWith('calendar-') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleEventCompleted(event.id)}
                              className="h-6 w-6 p-0"
                            >
                              {event.completed ? '✓' : '○'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Timeline view of upcoming events */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="text-white">Prossimi Eventi</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredEvents
              .filter(event => new Date(event.date) >= new Date())
              .slice(0, 10)
              .map((event) => {
                const IconComponent = eventTypeIcons[event.type];
                return (
                  <div
                    key={event.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${eventTypeColors[event.type]} ${
                      event.completed ? 'opacity-60' : ''
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{event.title}</span>
                        <Badge variant="secondary" className="text-xs">
                          {eventTypeLabels[event.type]}
                        </Badge>
                      </div>
                      <div className="text-sm opacity-80">
                        {format(parseISO(event.date), 'dd/MM/yyyy')}
                        {event.time && ` alle ${event.time}`}
                        {event.description && ` • ${event.description}`}
                      </div>
                    </div>
                    {event.id.startsWith('calendar-') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleEventCompleted(event.id)}
                        className="h-8 w-8 p-0"
                      >
                        {event.completed ? '✓' : '○'}
                      </Button>
                    )}
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};